// Koach My page component: renders account sections including Stadia (local/dev only).
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";

import { getFormattedDateValue } from "helpers/Datetime.helper";
import { getCurrentLangName, getCurrentLanguageCode } from "helpers/String.helper";
import { formatTimeAgo } from "helpers/Datetime.helper";

import PushCheckContainer from "pages/student/v2/my/PushCheck.container";
import { INoticeItem, INoticeContent } from "api/koach/v2/my.api";

// 스켈레톤 컴포넌트
const SkeletonSection = () => (
    <section className="bg-white rounded-2xl p-6 shadow-sm mb-6 animate-pulse">
        <div className="bg-gray-200 rounded h-6 w-48 mb-4"></div>
        <div className="space-y-4">
            <div className="space-y-2">
                <div className="bg-gray-200 rounded h-4 w-24"></div>
                <div className="bg-gray-200 rounded h-5 w-64"></div>
            </div>
            <div className="bg-gray-200 rounded h-10 w-full"></div>
        </div>
    </section>
);

const SkeletonToggleSection = () => (
    <section className="bg-white rounded-2xl p-6 shadow-sm mb-6 animate-pulse">
        <div className="flex items-center justify-between mb-4">
            <div className="bg-gray-200 rounded h-6 w-32"></div>
            <div className="w-14 h-7 bg-gray-200 rounded-full"></div>
        </div>
        <div className="space-y-2">
            <div className="bg-gray-200 rounded h-4 w-full"></div>
            <div className="bg-gray-200 rounded h-4 w-3/4"></div>
            <div className="bg-gray-200 rounded h-4 w-1/2"></div>
        </div>
    </section>
);

const SkeletonButtonSection = () => (
    <section className="space-y-4 animate-pulse">
        <div className="bg-gray-200 rounded-lg h-12 w-full"></div>
        <div className="bg-gray-200 rounded-lg h-12 w-full"></div>
    </section>
);

const MyComponent = (props: any) => {
    const navigate = useNavigate();
    const {
        isInit,
        isApp,
        email,
        info,
        stadiaBalance,
        isStadiaLoading,
        sendVerifyEmail,
        logout,
        isOnSendingEmail,
        isOnToggle,
        onToggleActivateAccount,
    } = props;

    const currentLangCode = getCurrentLanguageCode();

    const SHOW_STADIA =
        process.env.REACT_APP_ENV === "local" ||
        process.env.REACT_APP_ENV === "dev" ||
        process.env.REACT_APP_ENV === "development";
    
    // API 응답에서 공지사항 목록 가져오기 (최대 3개)
    const noticeList: INoticeItem[] = info?.noticeList?.slice(0, 3) || [];

    return (
        <main className="bg-gray-50 min-h-screen py-6">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="fixed top-0 left-0 w-full bg-gray-50 z-10">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-4 py-4">
                        <button onClick={() => navigate('/koach')} className="text-gray-600 hover:text-gray-900">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <h1 className="text-2xl font-bold text-gray-900">
                            {getLanguageValue(PAGE_TYPE.KOACH, "myMenu.txtTitle") || "마이페이지"}
                        </h1>
                    </div>
                </div>
                <div className="pt-16"></div>

                {!isInit ? (
                    <div className="space-y-6">
                        {/* 계정 정보 섹션 스켈레톤 */}
                        <SkeletonSection />
                        
                        {/* 휴대전화 정보 섹션 스켈레톤 */}
                        <SkeletonSection />
                        
                        
                        {/* 푸시 관련 섹션 스켈레톤 (앱인 경우) */}
                        {isApp && <SkeletonSection />}
                        
                        {/* 계정 관리 섹션 스켈레톤 */}
                        <SkeletonButtonSection />
                    </div>
                ) : (
                    <>
                        {info ? (
                            <>
                                {/* 공지사항 섹션 */}
                                <section className="bg-white rounded-2xl p-6 shadow-sm mb-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="text-lg font-semibold">
                                            공지사항
                                        </h2>
                                        {noticeList.length > 0 && (
                                            <button
                                                onClick={() => navigate("/koach/my/notice")}
                                                className="text-sm text-[#CF3688] hover:text-[#B82F77] font-medium"
                                            >
                                                전체보기
                                            </button>
                                        )}
                                    </div>
                                    {noticeList.length === 0 ? (
                                        <div className="text-center py-6 text-gray-500 text-sm">
                                            공지사항이 없습니다.
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {noticeList.map((notice: INoticeItem, index: number) => {
                                                const currentContent = notice.contents?.find(
                                                    (content: INoticeContent) => content.languageCode === currentLangCode
                                                ) || notice.contents?.find(
                                                    (content: INoticeContent) => content.languageCode === 'en'
                                                ) || notice.contents?.[0];

                                                if (!currentContent) return null;

                                                const noticeId = currentContent.noticeId;

                                                return (
                                                    <Link
                                                        key={`notice-${noticeId}-${index}`}
                                                        to={`/koach/my/notice/${noticeId}`}
                                                        className="block border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow cursor-pointer"
                                                    >
                                                        <div className="flex items-start justify-between">
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <div className="relative flex-1 min-w-0">
                                                                        <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">
                                                                            {currentContent.title}
                                                                        </h3>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
                                                                    <span>{formatTimeAgo(notice.createdAt)}</span>
                                                                </div>
                                                            </div>
                                                            <div className="ml-2 flex-shrink-0">
                                                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                                                </svg>
                                                            </div>
                                                        </div>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    )}
                                </section>

                                {/* 스타디아 섹션 */}
                                {SHOW_STADIA && (
                                    <section className="bg-white rounded-2xl p-6 shadow-sm mb-6">
                                    <h2 className="text-lg font-semibold mb-4">
                                        {getLanguageValue(PAGE_TYPE.KOACH, "my.txtSectionStadiaTitle") || "스타디아"}
                                    </h2>
                                    <div className="mb-4">
                                        <p className="text-gray-500 text-sm mb-1">
                                            {getLanguageValue(PAGE_TYPE.KOACH, "my.txtSectionStadiaBalanceLabel") || "현재 잔액"}
                                        </p>
                                        <p className="text-xl font-semibold text-gray-900">
                                            {isStadiaLoading ? "..." : (stadiaBalance !== null ? Number(stadiaBalance).toLocaleString("ko-KR") : "-")}
                                        </p>
                                    </div>
                                    <p className="text-gray-500 text-sm mb-4">
                                        {getLanguageValue(PAGE_TYPE.KOACH, "my.txtSectionStadiaDesc") || "플랫폼 내 결제 단위인 스타디아 잔액과 사용 내역을 확인하세요."}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => navigate("/koach/my/stadia")}
                                        className="w-full py-2 px-4 bg-[#CF3688] hover:bg-[#B82F77] text-white rounded-lg transition-colors text-sm text-center"
                                    >
                                        {getLanguageValue(PAGE_TYPE.KOACH, "my.btnSectionStadiaView") || "잔액·내역 보기"}
                                    </button>
                                    </section>
                                )}

                                {/* 계정 정보 섹션 */}
                                <section className="bg-white rounded-2xl p-6 shadow-sm mb-6">
                                    <h2 className="text-lg font-semibold mb-4">
                                        계정 정보
                                    </h2>
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-gray-500 text-sm">이메일</p>
                                                <p className="font-medium">{email}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-gray-500 text-sm">이름</p>
                                                <p className="font-medium">{info.userKoachInfo?.name || '-'}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-gray-500 text-sm">코치 타입</p>
                                                <p className="font-medium">
                                                    {info.userKoachInfo?.koachTypeInfo?.names 
                                                        ? getCurrentLangName(info.userKoachInfo.koachTypeInfo.names) 
                                                        : '-'}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-gray-500 text-sm">국적</p>
                                                <p className="font-medium">
                                                    {info.userKoachInfo?.countryInfo?.names 
                                                        ? getCurrentLangName(info.userKoachInfo.countryInfo.names) 
                                                        : '-'}
                                                </p>
                                            </div>
                                        </div>

                                        {/* 이메일 인증 */}
                                        {!info.hasVerifiedEmail && (
                                            <>
                                                <div className="text-amber-600 bg-amber-50 p-3 rounded-lg text-sm">
                                                    <p>
                                                        이메일 인증이 필요합니다.
                                                    </p>
                                                </div>
                                                <div className="flex gap-4">
                                                    <button
                                                        onClick={() => sendVerifyEmail()}
                                                        disabled={isOnSendingEmail}
                                                        className="w-full py-2 px-4 bg-[#CF3688] hover:bg-[#B82F77] text-white rounded-lg transition-colors text-sm text-center"
                                                    >
                                                        인증 메일 보내기
                                                    </button>
                                                </div>
                                            </>
                                        )}

                                        {/* 비밀번호 변경 */}
                                        <div className="flex gap-4">
                                            <button
                                                onClick={() => navigate("/koach/my/password")}
                                                className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm text-center">
                                                비밀번호 변경
                                            </button>
                                        </div>
                                    </div>
                                </section>

                                {/* 휴대전화 정보 섹션 */}
                                <section className="bg-white rounded-2xl p-6 shadow-sm mb-6">
                                    <h2 className="text-lg font-semibold mb-4">
                                        휴대전화 정보
                                    </h2>
                                    <div className="space-y-4">
                                        {info.userKoachInfo?.countryPhoneCode &&
                                            info.userKoachInfo?.cellphone ? (
                                            <div>
                                                <p className="text-gray-500 text-sm">휴대전화</p>
                                                <p className="font-medium">{info.userKoachInfo.countryPhoneCode} {info.userKoachInfo.cellphone}</p>
                                            </div>
                                        ) : (
                                            <div>
                                                <p className="text-gray-500 text-sm">
                                                    등록된 휴대전화가 없습니다.
                                                </p>
                                            </div>
                                        )}

                                        <div className="flex gap-4">
                                            <button
                                                onClick={() => {
                                                    navigate("/koach/my/cellphone");
                                                }}
                                                className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm text-center"
                                            >
                                                휴대전화 정보 수정
                                            </button>
                                        </div>
                                    </div>
                                </section>


                                {/* 푸시 관련 */}
                                {isApp && (
                                    <section className="bg-white rounded-2xl p-6 shadow-sm mb-6">
                                        <h2 className="text-lg font-semibold mb-4">
                                            알림 설정
                                        </h2>
                                        <div className="space-y-4">
                                            <PushCheckContainer />
                                        </div>
                                    </section>
                                )}

                                {/* 약관 및 정책 섹션 */}
                                <section className="bg-white rounded-2xl p-6 shadow-sm mb-6">
                                    <h2 className="text-lg font-semibold mb-4">
                                        약관 및 정책
                                    </h2>
                                    <div className="space-y-4">
                                        {info.termsList && info.termsList.length > 0 ? (
                                            info.termsList.map((item: any, idx: number) => (
                                                <button 
                                                    key={`term-${idx}`}
                                                    onClick={() => navigate(`/koach/my/terms/${item.id}`)}
                                                    className="block w-full text-left text-gray-700 hover:text-gray-900 py-2 border-b border-gray-100 last:border-b-0"
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm font-medium">{item.title}</span>
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-gray-400">
                                                            <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </div>
                                                </button>
                                            ))
                                        ) : (
                                            <p className="text-gray-500 text-sm">
                                                약관이 없습니다.
                                            </p>
                                        )}
                                    </div>
                                </section>

                                {/* 계정 관리 섹션 */}
                                <section className="space-y-4">
                                    <button
                                        onClick={() => navigate("/koach/my/delete-account")}
                                        className="w-full py-3 px-4 border border-[#CF3688] text-[#CF3688] hover:bg-[#CF3688] hover:text-white rounded-lg transition-colors"
                                    >
                                        회원 탈퇴
                                    </button>

                                    <Link
                                        to="/logout"
                                        className="block w-full py-3 px-4 border bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-center"
                                    >
                                        로그아웃
                                    </Link>
                                </section>
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-12">
                                <p className="text-gray-500 text-lg">
                                    정보를 불러올 수 없습니다.
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </main>
    );
};
export default MyComponent;
